import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { naira, compactNaira } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import {
  TrendingUp, Wallet, AlertTriangle, Percent, Layers, Receipt, Users, Clock,
  ClipboardList, Package, Sparkles, RefreshCw, AlertCircle, Eye, Lightbulb,
  TrendingUp as TrendUp, ChevronRight, Info,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const C = {
  panel: "#FFFFFF", border: "#EFE4D0", borderSoft: "#F5EDDC",
  text: "#3B2722", muted: "#8B7355", softMuted: "#A58F7C",
  burgundy: "#8B1E1E", gold: "#C49A4A", green: "#2E7D32", greenTint: "#E4F1E4",
  amber: "#D97706", amberTint: "#FEF3D8", red: "#C62828", redTint: "#FBE4E4",
};

const INSIGHT_STYLES = {
  action_required: { label: "ACTION REQUIRED", Icon: AlertCircle, color: C.red, tint: "#FDECEC", ring: "#F1BABA" },
  watch: { label: "WATCH", Icon: Eye, color: C.amber, tint: "#FEF3D8", ring: "#F0DDA6" },
  positive: { label: "POSITIVE", Icon: TrendUp, color: C.green, tint: "#E4F1E4", ring: "#B7DBBA" },
  opportunity: { label: "OPPORTUNITY", Icon: Lightbulb, color: C.burgundy, tint: "#FBEBEB", ring: "#E8C7C7" },
};

const DOMAIN_STYLE = {
  sales: { Icon: TrendingUp, color: "#B4570A", bg: "#FEF0DA" },
  finance: { Icon: Wallet, color: C.burgundy, bg: "#FBEBEB" },
  hr: { Icon: Users, color: "#6E28A6", bg: "#F2E7FD" },
  ops: { Icon: ClipboardList, color: "#00897B", bg: "#DBEEEB" },
  inventory: { Icon: Layers, color: "#1E5FBF", bg: "#E7F1FE" },
  shift: { Icon: Clock, color: "#EF6C00", bg: "#FDE7CE" },
};

const iso = (d) => d.toISOString().slice(0, 10);
function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "today") return { from: iso(today), to: iso(today), label: "Today" };
  if (preset === "week") {
    const dow = (today.getDay() + 6) % 7;
    const start = new Date(today); start.setDate(today.getDate() - dow);
    return { from: iso(start), to: iso(today), label: "This week" };
  }
  if (preset === "last_month") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: iso(start), to: iso(end), label: "Last month" };
  }
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: iso(start), to: iso(today), label: "Month to date" };
}

function fmt(v, format) {
  if (v === null || v === undefined) return "—";
  if (format === "money") return compactNaira(v);
  if (format === "pct") return `${Number(v).toFixed(1)}%`;
  if (format === "mins") return `${Number(v).toFixed(1)} min`;
  return Number.isFinite(+v) ? Number(v).toLocaleString() : String(v);
}

function Shell({ w, children, onClick, className = "" }) {
  return (
    <div
      role={onClick ? "link" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => onClick && (e.key === "Enter" || e.key === " ") && onClick()}
      data-testid={`widget-${w.key}`}
      className={`rounded-xl bg-white border p-4 flex flex-col gap-2 transition-shadow ${onClick ? "cursor-pointer hover:shadow-md hover:border-[#C49A4A]" : ""} ${className}`}
      style={{ borderColor: C.border, boxShadow: "0 1px 2px rgba(59,39,34,0.03)" }}
    >
      {children}
    </div>
  );
}

function MetricWidget({ w, go }) {
  const s = DOMAIN_STYLE[w.domain] || DOMAIN_STYLE.ops;
  const Icon = s.Icon;
  const delta = w.delta_pct;
  return (
    <Shell w={w} onClick={go}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: s.bg, color: s.color }}>
        <Icon size={18} strokeWidth={2} />
      </div>
      <div className="text-[12px] font-medium" style={{ color: C.muted }}>{w.label}</div>
      {w.empty ? (
        <div className="text-[13px] font-semibold" style={{ color: C.softMuted }} data-testid={`widget-${w.key}-empty`}>
          {w.empty_text || "No data yet"}
        </div>
      ) : (
        <>
          <div className="font-brand font-bold text-[24px] leading-tight" style={{ color: C.text }}>{fmt(w.value, w.format)}</div>
          {w.sub && <div className="text-[11px]" style={{ color: C.softMuted }}>{w.sub}</div>}
          {delta !== null && delta !== undefined && (
            <div className="text-[12px] font-semibold" style={{ color: (w.better === "down" ? delta <= 0 : delta >= 0) ? C.green : C.red }}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

function ListWidget({ w, go }) {
  const rows = w.rows || [];
  return (
    <Shell w={w} className="gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-brand font-bold text-base" style={{ color: C.text }}>{w.label}</h3>
        {!w.empty && w.value !== undefined && w.format !== "money" && (
          <span className="font-brand font-bold text-[15px]" style={{ color: C.burgundy }}>{fmt(w.value, w.format)}</span>
        )}
      </div>
      {w.sub && <div className="text-[11px] -mt-2" style={{ color: C.softMuted }}>{w.sub}</div>}
      {w.empty || rows.length === 0 ? (
        <div className="text-[12.5px] py-5 text-center" style={{ color: C.softMuted }} data-testid={`widget-${w.key}-empty`}>
          {w.empty_text || "No data yet"}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-[12.5px]">
              <span className="truncate" style={{ color: C.text }}>{r.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                {r.status && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: C.amberTint, color: "#B45309" }}>
                    {String(r.status).replace(/_/g, " ")}
                  </span>
                )}
                <span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>
                  {typeof r.value === "number" ? fmt(r.value, w.format) : (r.value || "")}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      {go && <button onClick={go} className="text-[12px] font-semibold flex items-center gap-1 mt-auto pt-1" style={{ color: C.gold }} data-testid={`widget-${w.key}-open`}>Open module <ChevronRight size={13} /></button>}
    </Shell>
  );
}

function BarsWidget({ w, go }) {
  const rows = w.rows || [];
  const max = Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <Shell w={w} className="gap-3">
      <h3 className="font-brand font-bold text-base" style={{ color: C.text }}>{w.label}</h3>
      {w.empty || rows.length === 0 ? (
        <div className="text-[12.5px] py-6 text-center" style={{ color: C.softMuted }} data-testid={`widget-${w.key}-empty`}>{w.empty_text || "No data yet"}</div>
      ) : (
        <div className="space-y-2.5">
          {rows.slice(0, 8).map((r, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-24 text-[12px] truncate" style={{ color: C.text }}>{r.name}</div>
              <div className="flex-1 h-2 rounded-full" style={{ background: "#F1E7D3" }}>
                <div className="h-full rounded-full" style={{ width: `${Math.max(4, (r.value / max) * 100)}%`, background: C.green }} />
              </div>
              <div className="w-20 text-right text-[12px] font-brand font-bold tabular-nums" style={{ color: C.text }}>{fmt(r.value, w.format)}</div>
            </div>
          ))}
        </div>
      )}
      {go && <button onClick={go} className="text-[12px] font-semibold flex items-center gap-1 mt-auto pt-1" style={{ color: C.gold }} data-testid={`widget-${w.key}-open`}>Open module <ChevronRight size={13} /></button>}
    </Shell>
  );
}

function TrendWidget({ w }) {
  const rows = w.rows || [];
  return (
    <Shell w={w} className="gap-3">
      <h3 className="font-brand font-bold text-base" style={{ color: C.text }}>{w.label}</h3>
      {w.empty || rows.length === 0 ? (
        <div className="text-[12.5px] py-8 text-center" style={{ color: C.softMuted }} data-testid={`widget-${w.key}-empty`}>{w.empty_text || "No data yet"}</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={rows} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${w.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.burgundy} stopOpacity={0.2} />
                <stop offset="100%" stopColor={C.burgundy} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1E7D3" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.softMuted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: C.softMuted }} axisLine={false} tickLine={false} width={62}
              tickFormatter={(v) => (w.format === "money" ? compactNaira(v) : v)} />
            <Tooltip formatter={(v) => (w.format === "money" ? naira(v) : v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
            <Area type="monotone" dataKey="value" stroke={C.burgundy} strokeWidth={2.5} fill={`url(#g-${w.key})`} dot={{ r: 3, fill: C.burgundy, stroke: "#fff", strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Shell>
  );
}

function TableWidget({ w, go }) {
  const rows = w.rows || [];
  const cols = w.columns || [["name", "Name"], ["value", "Value"]];
  return (
    <Shell w={w} className="gap-3">
      <h3 className="font-brand font-bold text-base" style={{ color: C.text }}>{w.label}</h3>
      {w.empty || rows.length === 0 ? (
        <div className="text-[12.5px] py-6 text-center" style={{ color: C.softMuted }} data-testid={`widget-${w.key}-empty`}>{w.empty_text || "No data yet"}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest" style={{ color: C.softMuted }}>
                {cols.map(([k, lbl]) => <th key={k} className="pb-2 pr-3 whitespace-nowrap">{lbl}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((r, i) => (
                <tr key={i} className="border-t" style={{ borderColor: C.borderSoft }}>
                  {cols.map(([k]) => (
                    <td key={k} className="py-2 pr-3 text-[12.5px] whitespace-nowrap" style={{ color: C.text }}>
                      {typeof r[k] === "number" ? (k === "name" ? r[k] : r[k].toLocaleString()) : String(r[k] ?? "—").replace(/_/g, " ")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {go && <button onClick={go} className="text-[12px] font-semibold flex items-center gap-1 mt-auto pt-1" style={{ color: C.gold }} data-testid={`widget-${w.key}-open`}>Open module <ChevronRight size={13} /></button>}
    </Shell>
  );
}

function InsightCard({ insight, onAction }) {
  const style = INSIGHT_STYLES[insight.kind] || INSIGHT_STYLES.watch;
  const Icon = style.Icon;
  return (
    <div className="rounded-lg bg-white border overflow-hidden flex flex-col" style={{ borderColor: style.ring }} data-testid={`insight-${insight.kind}`}>
      <div className="px-4 pt-3">
        <div className="text-[10px] font-bold tracking-widest flex items-center gap-1.5" style={{ color: style.color }}>
          <Icon size={12} strokeWidth={2.5} />{style.label}
        </div>
        <div className="font-brand font-bold text-[15px] mt-2 leading-tight" style={{ color: C.text }}>{insight.title}</div>
        <div className="text-[12.5px] mt-1.5 leading-snug" style={{ color: C.muted }}>{insight.body}</div>
      </div>
      <div className="mt-auto px-3 pt-3 pb-3 flex gap-2 flex-wrap">
        {(insight.actions || []).map((a, i) => (
          <button key={i} onClick={() => onAction?.(a)} data-testid={`insight-action-${insight.kind}-${i}`}
            className="text-[11.5px] font-semibold px-3 h-8 rounded-md"
            style={i === 0 ? { background: style.color, color: "#fff" } : { background: "#fff", color: style.color, border: `1px solid ${style.ring}` }}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Role-scoped dashboard. Renders ONLY the widgets the backend returns for the
 * signed-in role. `domain` narrows the set for a module landing page.
 */
export default function RoleDashboard({ domain = null, heading = null }) {
  const navigate = useNavigate();
  const storeKey = `sm_roledash_${domain || "main"}`;
  const [preset, setPreset] = useState(() => {
    try { return window.sessionStorage.getItem(`${storeKey}_preset`) || "mtd"; } catch { return "mtd"; }
  });
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loc, setLoc] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);

  useEffect(() => {
    try { window.sessionStorage.setItem(`${storeKey}_preset`, preset); } catch { /* ignore */ }
  }, [preset, storeKey]);

  const range = useMemo(() => {
    if (preset === "custom") {
      return { from: customFrom, to: customTo, label: customFrom && customTo ? `${customFrom} → ${customTo}` : "Custom range" };
    }
    return computeRange(preset);
  }, [preset, customFrom, customTo]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    if (loc !== "all") params.location_id = loc;
    if (domain) params.domain = domain;
    api.get("/dashboard/role-view", { params })
      .then(setData)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [range.from, range.to, loc, domain]);

  useEffect(() => { load(); }, [load]);

  const loadIntel = useCallback(async (force = false) => {
    setIntelLoading(true);
    try {
      setIntel(await api.get("/dashboard/intelligence", { params: force ? { refresh: 1 } : {} }));
    } catch { /* intelligence is optional */ }
    finally { setIntelLoading(false); }
  }, []);

  useEffect(() => { if (data?.show_intelligence && !intel) loadIntel(false); }, [data, intel, loadIntel]);

  const linkFor = useCallback((w) => {
    const p = new URLSearchParams();
    if (range.from) p.set("from", range.from);
    if (range.to) p.set("to", range.to);
    if (loc !== "all") p.set("location", loc);
    const qs = p.toString();
    return qs ? `${w.link}?${qs}` : w.link;
  }, [range.from, range.to, loc]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="role-dashboard-loading">
        {[...Array(8)].map((_, i) => <div key={i} className="h-36 rounded-xl border bg-white animate-pulse" style={{ borderColor: C.border }} />)}
      </div>
    );
  }
  if (!data) return null;

  const widgets = data.widgets || [];
  const metrics = widgets.filter((w) => w.kind === "metric");
  const panels = widgets.filter((w) => w.kind !== "metric");
  const insights = intel?.insights || [];

  return (
    <div className="space-y-5" data-testid="role-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-brand font-bold text-xl md:text-2xl" style={{ color: C.text }}>
            {heading || "Dashboard"}
          </h1>
          <div className="text-[12px]" style={{ color: C.softMuted }} data-testid="role-dashboard-role">
            {data.role || "—"} · {data.scope === "branch" ? "Your branch" : data.scope === "region" ? "Your region" : "All locations"} · {range.label}
          </div>
        </div>
      </div>

      {data.show_filters && (
        <div className="rounded-xl bg-white border p-3 flex flex-wrap items-center gap-2" style={{ borderColor: C.border }} data-testid="role-filter-bar">
          <div className="text-[10px] font-bold tracking-[0.14em] mr-1" style={{ color: C.softMuted }}>PERIOD</div>
          {[["today", "Today"], ["week", "This Week"], ["mtd", "Month to Date"], ["last_month", "Last Month"], ["custom", "Custom"]].map(([k, label]) => {
            const on = preset === k;
            return (
              <button key={k} onClick={() => setPreset(k)} data-testid={`role-preset-${k}`}
                className="h-8 px-3 rounded-md text-[12px] font-semibold border"
                style={on ? { background: C.burgundy, color: "#fff", borderColor: C.burgundy } : { background: "#fff", color: C.text, borderColor: C.border }}>
                {label}
              </button>
            );
          })}
          {preset === "custom" && (
            <div className="flex items-center gap-1.5">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="role-custom-from"
                className="h-8 px-2 rounded-md text-[12px] border" style={{ borderColor: C.border }} />
              <span className="text-[12px]" style={{ color: C.softMuted }}>→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="role-custom-to"
                className="h-8 px-2 rounded-md text-[12px] border" style={{ borderColor: C.border }} />
            </div>
          )}
          <div className="flex-1 min-w-2" />
          {!data.location_locked && (
            <>
              <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: C.softMuted }}>LOCATION</div>
              <Select value={loc} onValueChange={setLoc}>
                <SelectTrigger className="h-8 w-44 text-[12px]" data-testid="role-location"><SelectValue placeholder="All Locations" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {(data.locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      )}

      {widgets.length === 0 && (
        <div className="rounded-xl bg-white border p-10 text-center" style={{ borderColor: C.border }} data-testid="role-dashboard-none">
          <Info size={22} style={{ color: C.softMuted }} className="mx-auto mb-2" />
          <div className="font-brand font-bold text-base" style={{ color: C.text }}>No dashboard metrics for your role</div>
          <div className="text-[12.5px] mt-1" style={{ color: C.softMuted }}>Use the side menu to reach the screens you have access to.</div>
        </div>
      )}

      {metrics.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="role-metrics">
          {metrics.map((w) => <MetricWidget key={w.key} w={w} go={() => navigate(linkFor(w))} />)}
        </div>
      )}

      {data.show_intelligence && (insights.length > 0 || intelLoading) && (
        <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }} data-testid="sirmonty-intelligence">
          <div className="px-5 pt-4 pb-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: C.burgundy }}><Sparkles size={13} color="#fff" strokeWidth={2.5} /></div>
            <div className="font-brand font-bold text-lg" style={{ color: C.burgundy }}>Sir Monty Intelligence</div>
            <button onClick={() => loadIntel(true)} disabled={intelLoading} data-testid="intel-refresh"
              className="ml-auto text-[12px] font-semibold flex items-center gap-1.5 px-2 h-7 rounded-md hover:bg-[#FAF6EC] disabled:opacity-50" style={{ color: C.muted }}>
              <RefreshCw size={12} strokeWidth={2.5} className={intelLoading ? "animate-spin" : ""} />
              {intelLoading ? "Analysing…" : "Refresh"}
            </button>
          </div>
          {intel?.executive_summary && (
            <div className="px-5 py-4 text-[13.5px] leading-relaxed" style={{ background: "#FDF6E3", color: C.text }}>
              {intel.executive_summary}
            </div>
          )}
          {insights.length > 0 && (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {insights.slice(0, 4).map((ins, i) => (
                <InsightCard key={i} insight={ins} onAction={(a) => a?.module && navigate(`/${a.module}`)} />
              ))}
            </div>
          )}
        </div>
      )}

      {panels.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="role-panels">
          {panels.map((w) => {
            const go = () => navigate(linkFor(w));
            if (w.kind === "bars") return <BarsWidget key={w.key} w={w} go={go} />;
            if (w.kind === "trend") return <TrendWidget key={w.key} w={w} />;
            if (w.kind === "table") return <TableWidget key={w.key} w={w} go={go} />;
            return <ListWidget key={w.key} w={w} go={go} />;
          })}
        </div>
      )}
    </div>
  );
}
